import {
  TripMatch,
  TripMatchStatus,
  SalesStage,
  TripMessageState,
} from '@marageti/z4-sdk/lib/travel';
import React from 'react';
import {
  SvgIcon, Tooltip,
} from '@mui/material';
import { OutlinedIcons, CustomIcons, formatPhoneNumber } from '@marageti/z4-lib';
import { useAuthStore } from '../../../store';
import { calculateRemainingTime } from '../../../utils/helpers';
import { ReactComponent as SalesStage1 } from '../../../assets/svg/sales-01.svg';
import { ReactComponent as SalesStage2 } from '../../../assets/svg/sales-02.svg';
import { ReactComponent as SalesStage3 } from '../../../assets/svg/sales-03.svg';

const {
  MessageNoNewIcon,
  MessageErrorIcon,
  MessageTimerOrangeIcon,
  MessageNewIcon,
} = CustomIcons;

const {
  OutlinedPhoneIcon,
  OutlinedPhoneMissedCallIcon,
} = OutlinedIcons;

export const PhoneIndicator = ({
  tripMatch,
}: {
  tripMatch: TripMatch
}) => {
  const {
    prefix,
    number,
  } = tripMatch.travelers[0]?.phone || {};
  const phoneNumber = formatPhoneNumber(prefix, number);

  return (
    <Tooltip title={phoneNumber ? 'Phone Number Provided' : 'Missing Phone Number'}>
      <span>
        <SvgIcon
          color={phoneNumber ? 'primary' : 'disabled'}
          component={phoneNumber ? OutlinedPhoneIcon : OutlinedPhoneMissedCallIcon}
        />
      </span>
    </Tooltip>
  );
};

const NotificationDot = () => (
  <span
    style={{
      height: '8px',
      width: '8px',
      backgroundColor: '#0089FC',
      borderRadius: '50%',
      display: 'inline-block',
      verticalAlign: 'middle',
    }}
  />
);

export const MinimalChatIndicator = ({
  loggedInAgentId,
  tripMessageState,
}: {
  loggedInAgentId: string,
  tripMessageState: TripMessageState,
}) => {
  const loggedInAgentMessageState = tripMessageState.find((m) => (
    m.personId !== ''
    && m.hasNew
    && m.personId === loggedInAgentId
  ));
  return loggedInAgentMessageState?.hasNew ? <NotificationDot /> : null;
};

export const ChatIndicator = ({
  tripMatch,
}: {
  tripMatch: TripMatch
}) => {
  const { loggedInAgent } = useAuthStore();
  const agentMessageState = tripMatch.tripMessageState.find((m) => m.personId !== '' && m.personId === loggedInAgent?.id);
  const hasNewMessage = agentMessageState?.hasNew || false;
  const timeRemaining = calculateRemainingTime(tripMatch.createdAt);
  const isStatusAcceptedOrPending = tripMatch.status === TripMatchStatus.MatchAccepted || tripMatch.status === TripMatchStatus.MatchPending;
  const MessageIcon = hasNewMessage ? MessageNewIcon : MessageNoNewIcon;

  // If the trip match is accepted or pending, show the first message reminder
  if (isStatusAcceptedOrPending) {
    if (timeRemaining <= 0) {
      return (
        <Tooltip title="Overdue">
          <span>
            <MessageErrorIcon sx={{ fill: 'none' }} />
          </span>
        </Tooltip>
      );
    }
    return (
      <Tooltip title={timeRemaining > 24 ? 'Traveler awaiting first message' : `${timeRemaining}h left to send the first message`}>
        <span>
          <MessageTimerOrangeIcon sx={{ fill: 'none' }} />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={hasNewMessage ? 'New message received' : 'No new messages'}>
      <span>
        <MessageIcon color={hasNewMessage ? 'primary' : 'disabled'} sx={{ fill: 'none' }} />
      </span>
    </Tooltip>
  );
};

const salesStageIcons = {
  [SalesStage.NeedsFollowUp]: { icon: <SalesStage1 />, tooltip: 'Needs Follow Up' }, // TODO: Deprecate
  [SalesStage.NeedsProposal]: { icon: <SalesStage2 />, tooltip: 'Needs Proposal' }, // TODO: Deprecate
  [SalesStage.ProposalSent]: { icon: <SalesStage3 />, tooltip: 'Proposal Sent' }, // TODO: Deprecate
  [SalesStage.FirstMessageSent]: { icon: <CustomIcons.SalesStage1Icon />, tooltip: 'Sales Stage: First Message' },
  [SalesStage.Consultation]: { icon: <CustomIcons.SalesStage2Icon />, tooltip: 'Sales Stage: Consultation' },
  [SalesStage.ItinerarySent]: { icon: <CustomIcons.SalesStage3Icon />, tooltip: 'Sales Stage: Proposal Sent' },
  [SalesStage.ItineraryInReview]: { icon: <CustomIcons.SalesStage4Icon />, tooltip: 'Sales Stage: Itinerary Review' },
};

export const SalesStageIndicator = ({ tripMatch }: {
  tripMatch: TripMatch
}) => {
  const { salesStage } = tripMatch;
  const { icon, tooltip } = salesStageIcons[salesStage] || { icon: <CustomIcons.SalesStage0Icon />, tooltip: 'Sales Stage: Undefined' };

  return (
    <Tooltip title={tooltip}>
      <span>
        {icon}
      </span>
    </Tooltip>
  );
};
